import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const CoachingStaffManagement = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const teamTypes = [
    { value: 'all', label: 'ყველა შტაბი' },
    { value: 'main_staff', label: 'პირველი გუნდის შტაბი' },
    { value: 'second_team', label: 'მეორე გუნდის შტაბი' },
    { value: 'womens_team', label: 'ქალთა გუნდის შტაბი' },
    { value: 'youth_academy', label: 'ასაკობრივი ჯგუფების შტაბი' }
  ];

  useEffect(() => {
    loadStaff();
  }, [selectedTeam]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      if (!token) {
        console.error('No auth token found');
        return;
      }

      const url = selectedTeam === 'all' 
        ? '/api/coaching-staff/' 
        : `/api/coaching-staff/?team_type=${selectedTeam}`;

      const response = await fetch(getApiUrl(url), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      } else {
        console.error('Failed to load coaching staff');
      }
    } catch (error) {
      console.error('Error loading coaching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ მწვრთნელის წაშლა?')) {
      return;
    }

    try {
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const response = await fetch(getApiUrl(`/api/coaching-staff/${staffId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setStaff(staff.filter(s => s.id !== staffId));
      } else {
        alert('მწვრთნელის წაშლა ვერ მოხერხდა');
      }
    } catch (error) {
      console.error('Error deleting coaching staff:', error);
      alert('შეცდომა მწვრთნელის წაშლისას');
    }
  };

  const filteredStaff = staff.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTeamTypeLabel = (teamType) => {
    const team = teamTypes.find(t => t.value === teamType);
    return team ? team.label : teamType;
  };

  const getLicenseColor = (license) => {
    switch (license) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'B': return 'bg-yellow-100 text-yellow-800';
      case 'C': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <AcademicCapIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  სამწვრთნელო შტაბის მართვა
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  მწვრთნელების დამატება, რედაქტირება და წაშლა
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/_panel-7c39f8/coaching-staff/new')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              ახალი მწვრთნელი
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  შტაბი
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  {teamTypes.map(team => (
                    <option key={team.value} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ძებნა
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="მწვრთნელის სახელი ან პოზიცია..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Staff List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-4">
              სამწვრთნელო შტაბი ({filteredStaff.length})
            </h3>
            
            {filteredStaff.length === 0 ? (
              <div className="text-center py-12">
                <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">
                  მწვრთნელები ვერ მოიძებნა
                </h3>
                <p className="mt-1 text-sm text-gray-500 font-georgian">
                  დაამატეთ ახალი მწვრთნელი ან შეცვალეთ ძებნის კრიტერიუმები
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredStaff.map((member) => (
                  <div key={member.id} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {member.name.charAt(0)}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/_panel-7c39f8/coaching-staff/${member.id}/edit`)}
                          className="text-gray-400 hover:text-primary-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 font-georgian text-lg mb-2">
                      {member.name}
                    </h3>
                    
                    <div className="space-y-2">
                      <p className="text-sm text-primary-600 font-georgian font-medium">
                        {member.position}
                      </p>
                      
                      <p className="text-sm text-gray-600 font-georgian">
                        <span className="font-medium">შტაბი:</span> {getTeamTypeLabel(member.team_type)}
                      </p>
                      
                      {member.age && (
                        <p className="text-sm text-gray-600 font-georgian">
                          <span className="font-medium">ასაკი:</span> {member.age} წელი
                        </p>
                      )}
                      
                      <p className="text-sm text-gray-600 font-georgian">
                        <span className="font-medium">ეროვნება:</span> {member.nationality}
                      </p>
                      
                      {member.experience && (
                        <p className="text-sm text-gray-600 font-georgian">
                          <span className="font-medium">გამოცდილება:</span> {member.experience}
                        </p>
                      )}
                      
                      {member.license && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLicenseColor(member.license)}`}>
                          ლიცენზია {member.license}
                        </span>
                      )}
                    </div>
                    
                    {member.description && (
                      <p className="mt-3 text-sm text-gray-600 font-georgian line-clamp-2">
                        {member.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CoachingStaffManagement;
